import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { tokenNotExpired } from 'angular2-jwt';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
// for social
import { SocialAuthService } from 'angularx-social-login';
import {FacebookLoginProvider, GoogleLoginProvider} from 'angularx-social-login';
// services
import { StoreService } from 'src/app/services/store.service';
import { HttpService } from 'src/app/services/http.service';

@Injectable()
export class LoginService {
  loading = Swal.mixin({
    allowOutsideClick: false, allowEscapeKey: false
  });
  token: string;
  user: any;
  loggedIn: boolean = false;
  
  timer: number;
  uId: string;
  // Preserve the identifier entered at sign-in so OTP login can default to it.
  loginIdentifier: string;
  private otpLoginEmailIdentifier: string = '';
  private otpLoginMobileIdentifier: string = '';

  constructor(
    private storeService: StoreService,
    private httpService: HttpService,
    private socialAuthService: SocialAuthService,
    private router: Router,
    @Inject(PLATFORM_ID) private readonly platformId: any
  ) {
    this.storeService.user.subscribe(res => {
      if (res) {
        this.user = res.data;
        this.loggedIn = res.isLogged;
        this.token = res.token;
      }
    }, err => {
      // console.log(err);
    });
    this.socialLoginResponse();
  }

  IsloggedIn() {
    if (this.platformId !== 'browser') {
      return false;
    }
    try {
      const token = localStorage.getItem('token');
      // Treat a missing or corrupted token (e.g. the literal "undefined"/"null"
      // strings a stale write could leave behind) as logged-out, and purge it so
      // it can never be restored on the next app bootstrap.
      if (!token || token === 'undefined' || token === 'null') {
        if (token) {
          this.userSessionOut();
        }
        return false;
      }
      return !!tokenNotExpired('token');
    } catch (e) {
      this.userSessionOut();
      return false;
    }
  }

  emailOrMobileLogin(data: any) {
    // console.log(data);
    if (this.platformId === 'browser') {
      this.loginIdentifier = data && data.email ? String(data.email).trim() : '';
      this.loading.fire({
        customClass: { container: 'progress-swal' },
        title: 'Please Wait...',
        onOpen: () => {
          Swal.showLoading();
        }
      });
      this.httpService.loginService(data).subscribe(res => {
        let _res: any = {};
        _res = res;
        if(_res['data']) {
          _res['data'].user_type = 'normal_user';
        }
        this.setLoginInfo(_res);
      }, err => {
        const errorResponse = err && err.error;
        if (errorResponse && (errorResponse.status === 'error' || errorResponse.status === false)) {
          // Backend returns business errors (not registered / wrong password) as
          // HTTP 4xx with { status:'error', msg, flag }. Route them through
          // setLoginInfo so the Sign Up / Sign In with OTP options are shown.
          this.setLoginInfo(errorResponse);
        } else {
          this.loading.fire({
            icon: 'error', title: 'Oops!', text: 'Please try again with correct credentials.',
            onOpen: () => {
              Swal.hideLoading();
            }
          });
        }
      });
    }
  }

  loginWithGoogle(): void {
    this.socialAuthService.signIn(GoogleLoginProvider.PROVIDER_ID);
  }

  loginWithFacebook(): void {
    this.socialAuthService.signIn(FacebookLoginProvider.PROVIDER_ID);
  }


  socialLoginResponse() {
    if (this.platformId === 'browser') {
      this.socialAuthService.authState.subscribe((user) => {
        if (user) {
          this.httpService.socialUserSignUp({
            first_name: user.firstName,
            last_name: user.lastName,
            photo: user.photoUrl,
            email: user.email,
            providerId: user.id,
            provider: user.provider
          }).subscribe(result => {
            if (result) {
              let _res: any = {};
              _res = result;
              if(_res['data']) {
                _res['data'].user_type = 'social_user';
              }
              this.setLoginInfo(_res);
            } else {
              this.loading.fire({
                icon: 'error', title: 'Oops!', text: 'Something went wrong!',
                onOpen: () => {
                  Swal.hideLoading();
                }
              });
            }
          });
        } else {
          Swal.fire('Warning!', 'Social login failed. Please login with email and password.', 'error');
          if (this.loading.isVisible()) {
            this.loading.close();
          }
        }
      }, err => {
        if (this.loading.isVisible()) {
          this.loading.close();
        }
      });
    }
  }

  setLoginInfo(result: any) {
    if (this.platformId === 'browser') {
      if ((result.status === true || result.status === 'success') && result.token !== '') {
        this.loggedIn = true;
        this.user = result['data'];
        if(!this.user.isVerified) {
          this.verifyAccount();
        }
        this.storeService.saveLoginData({ isLogged: true, data: result['data'], token: result['token'] });
        if (this.loading.isVisible()) {
          this.loading.close();
        }
        this.storeService.pageRedirection(this.user);
      } else {
        this.storeService.saveLoginData({isLogged: false});
        this.loading.fire({
          title: 'Oops!',
          text: result['msg'],
          showCloseButton: true,
          showCancelButton: true,
          confirmButtonColor: '#28a745',
          cancelButtonColor: '#212529',
          confirmButtonText: result['flag'] === 0 ? 'Sign Up' : 'Sign In with OTP',
          cancelButtonText: 'Close',
          onOpen: () => {
            Swal.hideLoading();
          }
        }).then((value) => {
          if (value.isConfirmed) {
            if(result['flag'] === 0) {
              this.router.navigate(['/sign-up']);
            } else {
              this.startOtpLogin(this.loginIdentifier);
            }
          }
        });
      }
    }
  }


  // ----- Sign in with a one-time code (OTP) after a wrong password -----

  // Opens an inline delivery-method selector. The channel matching the identifier
  // entered on the sign-in form is selected and prefilled by default.
  startOtpLogin(identifier?: string, alternateIdentifier?: string) {
    if (this.platformId !== 'browser') {
      return;
    }
    // Prefer an explicitly passed identifier, then the one captured at login time.
    let target = (identifier && String(identifier).trim()) || (this.loginIdentifier && String(this.loginIdentifier).trim());
    if (!target) {
      this.loading.fire({
        icon: 'warning',
        title: 'Oops!',
        text: 'Please enter your email or mobile number, then try signing in with OTP again.',
        onOpen: () => {
          Swal.hideLoading();
        }
      });
      return;
    }

    let alternateTarget = alternateIdentifier && String(alternateIdentifier).trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (alternateTarget && emailPattern.test(alternateTarget) && !emailPattern.test(target)) {
      const originalTarget = target;
      target = alternateTarget;
      alternateTarget = originalTarget;
    }
    this.openOtpDeliveryMethodModal(target, alternateTarget);
  }

  private openOtpDeliveryMethodModal(identifier: string, alternateIdentifier?: string): void {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobilePattern = /^\+?[0-9\s()\-]{7,20}$/;
    let selectedChannel = emailPattern.test(identifier) ? 'email' : 'mobile';
    const alternateIsEmail = alternateIdentifier && emailPattern.test(alternateIdentifier);
    const enteredValues = {
      email: selectedChannel === 'email'
        ? identifier
        : (alternateIsEmail ? alternateIdentifier : ''),
      mobile: selectedChannel === 'mobile'
        ? identifier
        : (alternateIdentifier && !alternateIsEmail ? alternateIdentifier : '')
    };

    Swal.fire({
      title: 'Sign in with OTP',
      html: `
        <div class="btn-group mb-4" role="group" aria-label="Choose how to receive your one-time code">
          <button type="button" id="otp-method-email" class="btn btn-outline-dark">By Email Address</button>
          <button type="button" id="otp-method-mobile" class="btn btn-outline-dark">By Mobile Number</button>
        </div>
        <div class="text-left">
          <label id="otp-destination-label" for="otp-destination-input"></label>
          <input id="otp-destination-input" class="form-control" />
        </div>`,
      confirmButtonText: selectedChannel === 'email' ? 'Send code to my email' : 'Send code to my mobile',
      confirmButtonColor: '#28a745',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
      focusConfirm: false,
      onOpen: () => {
        const emailButton = document.getElementById('otp-method-email') as HTMLButtonElement;
        const mobileButton = document.getElementById('otp-method-mobile') as HTMLButtonElement;
        const input = document.getElementById('otp-destination-input') as HTMLInputElement;
        const label = document.getElementById('otp-destination-label');

        const selectChannel = (channel: string) => {
          if (input) {
            enteredValues[selectedChannel] = input.value;
          }
          selectedChannel = channel;

          emailButton.classList.toggle('btn-dark', channel === 'email');
          emailButton.classList.toggle('btn-outline-dark', channel !== 'email');
          mobileButton.classList.toggle('btn-dark', channel === 'mobile');
          mobileButton.classList.toggle('btn-outline-dark', channel !== 'mobile');

          label.textContent = channel === 'email' ? 'Email Address *' : 'Mobile Number *';
          input.type = channel === 'email' ? 'email' : 'tel';
          input.inputMode = channel === 'email' ? 'email' : 'tel';
          input.autocomplete = channel === 'email' ? 'email' : 'tel';
          input.placeholder = channel === 'email' ? 'e.g. example@gmail.com' : 'e.g. 07123456789';
          input.value = enteredValues[channel];

          const confirmButton = Swal.getConfirmButton();
          if (confirmButton) {
            confirmButton.textContent = channel === 'email'
              ? 'Send code to my email'
              : 'Send code to my mobile';
          }
        };

        emailButton.addEventListener('click', () => selectChannel('email'));
        mobileButton.addEventListener('click', () => selectChannel('mobile'));
        input.value = enteredValues[selectedChannel];
        selectChannel(selectedChannel);
        input.focus();
      },
      preConfirm: () => {
        const input = document.getElementById('otp-destination-input') as HTMLInputElement;
        const destination = input ? input.value.trim() : '';

        if (!destination) {
          Swal.showValidationMessage(
            selectedChannel === 'email'
              ? 'Please enter your email address.'
              : 'Please enter your mobile number.'
          );
          return false;
        }
        if (selectedChannel === 'email' && !emailPattern.test(destination)) {
          Swal.showValidationMessage('Please enter a valid email address.');
          return false;
        }
        if (selectedChannel === 'mobile' && !mobilePattern.test(destination)) {
          Swal.showValidationMessage('Please enter a valid mobile number.');
          return false;
        }

        enteredValues[selectedChannel] = destination;
        this.otpLoginEmailIdentifier = enteredValues.email;
        this.otpLoginMobileIdentifier = enteredValues.mobile;
        return { identifier: destination, channel: selectedChannel };
      }
    }).then((result: any) => {
      if (result.value) {
        this.sendLoginOtp(result.value.identifier);
      }
    });
  }

  private sendLoginOtp(identifier: string): void {
    this.loginIdentifier = identifier;
    this.loading.fire({
      customClass: { container: 'progress-swal' },
      title: 'Sending your code...',
      onOpen: () => {
        Swal.showLoading();
      }
    });
    this.httpService.sendLoginOtp({ email: identifier }).subscribe(res => {
      const status = res && res['status'];
      if (status === 'sms_sent' || status === 'email_sent') {
        this.uId = res['uid'];
        this.timer = (1000 * 60) * 1;
        const channel = status === 'email_sent' ? 'email' : 'mobile number';
        this.openLoginOtpModal(channel, identifier);
      } else {
        this.loading.fire({
          icon: 'error',
          title: 'Oops!',
          text: (res && (res['msg'] || res['message'])) || 'We could not send your code. Please try again.',
          onOpen: () => {
            Swal.hideLoading();
          }
        });
      }
    }, err => {
      const errorResponse = err && err.error;
      this.loading.fire({
        icon: 'error',
        title: 'Oops!',
        text: (errorResponse && (errorResponse.msg || errorResponse.message)) || 'We could not send your code. Please try again.',
        onOpen: () => {
          Swal.hideLoading();
        }
      });
    });
  }

  openLoginOtpModal(channel: string, identifier: string = this.loginIdentifier) {
    if (this.platformId === 'browser') {
      let timerInterval: any;
      let secondsRemaining = Math.max(0, Math.ceil((this.timer || 60000) / 1000));
      const safeIdentifier = this.escapeHtml(identifier);

      const updateOtpActions = () => {
        const timerElement = document.getElementById('login-otp-resend-timer');
        const resendButton = Swal.getCancelButton() as HTMLButtonElement;
        const changeMethodButton = document.getElementById('login-otp-change-method') as HTMLButtonElement;

        if (timerElement) {
          if (secondsRemaining > 0) {
            const minutes = Math.floor(secondsRemaining / 60);
            const seconds = secondsRemaining % 60;
            timerElement.textContent = `You can resend or choose another method in ${minutes}:${seconds < 10 ? '0' : ''}${seconds}.`;
          } else {
            timerElement.textContent = 'You can resend or choose another method now.';
          }
        }

        if (resendButton) {
          resendButton.disabled = secondsRemaining > 0;
          resendButton.title = secondsRemaining > 0
            ? 'Please wait until the one-minute timer finishes.'
            : 'Send a new one-time code.';
        }

        if (changeMethodButton) {
          changeMethodButton.disabled = secondsRemaining > 0;
          changeMethodButton.title = secondsRemaining > 0
            ? 'You can choose another delivery method when the timer finishes.'
            : 'Choose email or mobile for your next OTP.';
        }
      };

      Swal.fire({
        title: 'Sign in with code',
        html: `We have sent a one-time code to your <strong>${channel}</strong> ` +
              `<strong>(${safeIdentifier})</strong>. Please enter it below to sign in.
              <div id="login-otp-resend-timer" style="margin-top:10px;font-size:13px;color:#6c757d;"></div>`,
        footer: `<button type="button" id="login-otp-change-method" class="btn btn-link btn-sm" disabled>
          Back to choosing how to receive your OTP
        </button>`,
        input: 'text',
        inputAttributes: {
          inputmode: 'numeric',
          autocomplete: 'one-time-code'
        },
        inputPlaceholder: 'One-time code *',
        confirmButtonText: 'Verify & Sign in',
        confirmButtonColor: '#28a745',
        showCancelButton: true,
        cancelButtonText: 'Resend OTP',
        showCloseButton: true,
        closeButtonAriaLabel: 'Close OTP sign in',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showLoaderOnConfirm: true,
        inputValidator: (value) => {
          if (!value) return 'Please enter your code.';
          if (value && (value.length < 4 || !/^[0-9]+$/.test(value))) return 'Please enter a valid code.';
        },
        preConfirm: (value) => {
          let data = {
            email: this.loginIdentifier,
            verification_token: value,
            uid: this.uId
          };

          return new Promise((resolve) => {
            this.httpService.verifyForgetPasswordToken(data).subscribe(res => {
              if (res['status'] === 'success') {
                this.uId = undefined;
                let _res: any = {};
                _res = res;
                if (_res['data']) {
                  _res['data'].user_type = 'normal_user';
                }
                this.setLoginInfo(_res);
                resolve(res);
              } else {
                Swal.showValidationMessage(
                  (res && (res['message'] || res['msg'])) ||
                  'Invalid one-time code, please check and try again.'
                );
                resolve(false);
                setTimeout(updateOtpActions, 0);
              }
            }, err => {
              Swal.showValidationMessage(
                (err && err.error && (err.error.message || err.error.msg)) ||
                'The code could not be verified. Please try again.'
              );
              resolve(false);
              setTimeout(updateOtpActions, 0);
            });
          });
        },
        onOpen: () => {
          const changeMethodButton = document.getElementById('login-otp-change-method') as HTMLButtonElement;
          changeMethodButton.addEventListener('click', () => {
            if (secondsRemaining > 0) {
              return;
            }
            Swal.close();
            setTimeout(() => this.reopenOtpDeliveryMethodModal(), 0);
          });

          updateOtpActions();
          timerInterval = setInterval(() => {
            secondsRemaining--;
            if (secondsRemaining <= 0) {
              secondsRemaining = 0;
              clearInterval(timerInterval);
            }
            this.timer = secondsRemaining * 1000;
            updateOtpActions();
          }, 1000);
        },
        onClose: () => {
          this.timer = secondsRemaining * 1000;
          clearInterval(timerInterval);
        }
      }).then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          this.sendLoginOtp(this.loginIdentifier);
        }
      });
    }
  }

  private reopenOtpDeliveryMethodModal(): void {
    const currentIsEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.loginIdentifier || '');
    const alternateIdentifier = currentIsEmail
      ? this.otpLoginMobileIdentifier
      : this.otpLoginEmailIdentifier;
    this.openOtpDeliveryMethodModal(this.loginIdentifier, alternateIdentifier);
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }




  logOut(): void {
    if (this.platformId === 'browser') {
      this.loading.fire({
        customClass: { container: 'progress-swal' },
        title: 'Please Wait...',
        onOpen: () => {
          Swal.showLoading();
        }
      });
      if(this.user['user_type'] === 'social_user') {
        this.socialAuthService.signOut();
      }
      this.userSessionOut();
      this.router.navigateByUrl('sign-in');
      if (this.loading.isVisible()) {
        this.loading.close();
      }
    }
  }

  userSessionOut() {
    if (this.platformId === 'browser') {
      this.storeService.saveLoginData({ isLogged: false });
      this.storeService.saveDeliveryAddress({});
      this.storeService.setDeliveryChargeInfo({});
    }
  }

  verifyAccount(secretToken?: string) {
    const data = {mobile_no: this.user.mobile_no, honeypot_field: ''};
    this.httpService.sendForgotOtp(data, secretToken).subscribe(res => {
      if (res['status'] === 'sms_sent') {
        this.uId = res['uid'];
        this.timer = (1000 * 60) * 1;
        this.openOTPModal();
      }
    }, err => {
    });
  }

  openOTPModal() {
    if (this.platformId === 'browser') {
      let timerInterval: any;   
      Swal.fire({
        title: 'Verify Account',
        html: `We have sent a SMS to <strong>${this.user.mobile_no}</strong> with your varification code.
              Please enter your code within <b></b>`,
        input: 'text',
        inputPlaceholder: 'Verification code *',
        confirmButtonText: 'Verify',
        showCancelButton: true,
        cancelButtonText: 'Skip now',
        allowOutsideClick: false,
        showLoaderOnConfirm: true,
        timer: this.timer,
        timerProgressBar: true,
        inputValidator: (value) => {
          if (!value) return 'Please enter your Code.'
          if (value && value.length < 4) return 'Please enter your valid Code.'
        },
        preConfirm: (value) => {
          let data = {
            email: this.user.email,
            verification_token: value,
            uid: this.uId
          }
          this.httpService.verifyForgetPasswordToken(data).subscribe(res => {
            this.uId = undefined;
            if (res['status'] === 'success') {
              let _res: any = {};
              _res = res;
              if(_res['data']) {
                _res['data'].user_type = 'normal_user';
              }
              this.setLoginInfo(_res);
            } else {
              this.resendOTPConfirmationModal(res['message']);
            }
          });
        },
        onOpen: () => {
          timerInterval = setInterval(() => {
            const content = Swal.getContent();
            if (content) {
              const b = content.querySelector('b');
              if (b) {
                let min = Math.floor((Swal.getTimerLeft() / (1000 * 60)) % 60);
                let sec = Math.floor((Swal.getTimerLeft() / 1000) % 60);
                b.textContent = (min + ':' + sec);
              }
            }
            this.timer = Swal.getTimerLeft();
          }, 1000);
        },
        onClose: () => {
          if(this.timer > 0) this.countdownOTPTime();
          clearInterval(timerInterval);
        }
      }).then((result) => {
        if (result.dismiss === Swal.DismissReason.timer) {
          this.resendOTPConfirmationModal();
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          this.skipOTPConfirmationModal();
        }
      })
    }
  }

  skipOTPConfirmationModal() {
    if (this.platformId === 'browser') {
      Swal.fire({
        title: 'Are you sure?',
        text: "You can't place order untill your acoount varification",
        icon: 'warning',
        confirmButtonText: 'Yes, Skip Now',
        showCancelButton: true,
        allowOutsideClick: false,
        allowEscapeKey: false
      }).then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          this.openOTPModal();
        }
      })
    }
  }

  resendOTPConfirmationModal(msg?: string) {
    if (this.platformId === 'browser') {
      Swal.fire({
        title: msg ? 'Notice' : 'Resend Again?',
        text: msg ? (msg + '. ' + 'Want to try again?') : 'You want to send OTP on your mobile again?',
        icon: msg ? 'info' : 'question',
        confirmButtonText: 'Resend OTP',
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          this.verifyAccount();
        }
      });
    }
  }

  countdownOTPTime() {
    if (this.platformId === 'browser') {
      if(this.timer > 0) {
        let timerInterval = setInterval(() => {
          this.timer = this.timer - 1000;
          if(this.timer <= 0) {
            clearInterval(timerInterval);
            this.uId = undefined;
          }
        }, 1000);
      }
    }
  }

}
